import { useState } from "react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Progress } from "@shared/components/ui/progress";
import { useCreateCity } from "./useCities";
import { useTranslation } from "react-i18next";
import {
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";

export function ImportExportCities({ cities = [] }) {
  const { t } = useTranslation();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const { mutate: createCity } = useCreateCity();

  const handleExport = () => {
    if (!cities.length) {
      toast.error(t("city.export.noCitiesToExport"));
      return;
    }

    const csvContent = [
      // Header
      [
        "Name",
        "Arabic Name",
        "Deadline",
        "Delivery Fees",
        "Cancellation Fees",
        "Refusal Fees",
        "Fixed Fees",
      ].join(","),
      // Data
      ...cities.map((city) =>
        [
          `"${city.name}"`,
          `"${city.nameArabic}"`,
          `"${city.deadline}"`,
          city.deliveryFees,
          city.cancellationFees || 0,
          city.refusalFees || 0,
          city.isFeesFixed ? "TRUE" : "FALSE",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `cities_export_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    toast.success(t("city.export.success", { count: cities.length }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error(t("city.import.invalidFileType"));
      return;
    }

    setImportFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim());

        if (lines.length < 2) {
          toast.error(t("city.import.emptyFile"));
          return;
        }

        // Skip header row
        const dataLines = lines.slice(1);
        const parsedData = [];

        dataLines.forEach((line, index) => {
          try {
            // Simple CSV parsing (handles quoted values)
            const values = line
              .split(",")
              .map((val) => val.trim().replace(/^"(.*)"$/, "$1"));

            if (values.length >= 7) {
              parsedData.push({
                name: values[0],
                nameArabic: values[1],
                deadline: values[2],
                deliveryFees: parseFloat(values[3]) || 0,
                cancellationFees: parseFloat(values[4]) || 0,
                refusalFees: parseFloat(values[5]) || 0,
                isFeesFixed: values[6].toLowerCase() === "true",
                rowIndex: index + 2, // +2 because we start from line 2 (after header)
              });
            }
          } catch (error) {
            console.warn(`Error parsing line ${index + 2}:`, error);
          }
        });

        setImportData(parsedData);
      } catch (error) {
        toast.error(t("city.import.parseError"));
        console.error("CSV parsing error:", error);
      }
    };

    reader.readAsText(file);
  };

  const validateImportData = (data) => {
    const errors = [];

    data.forEach((city) => {
      if (!city.name?.trim()) {
        errors.push(
          `${t("city.import.validation.missingName")} (${t("common.row")} ${
            city.rowIndex
          })`
        );
      }
      if (!city.nameArabic?.trim()) {
        errors.push(
          `${t("city.import.validation.missingArabicName")} (${t(
            "common.row"
          )} ${city.rowIndex})`
        );
      }
      if (!city.deadline?.trim()) {
        errors.push(
          `${t("city.import.validation.missingDeadline")} (${t("common.row")} ${
            city.rowIndex
          })`
        );
      }
      if (isNaN(city.deliveryFees) || city.deliveryFees < 0) {
        errors.push(
          `${t("city.import.validation.invalidDeliveryFees")} (${t(
            "common.row"
          )} ${city.rowIndex})`
        );
      }
    });

    return errors;
  };

  const handleImport = async () => {
    if (!importData.length) {
      toast.error(t("city.import.noDataToImport"));
      return;
    }

    const validationErrors = validateImportData(importData);
    if (validationErrors.length > 0) {
      toast.error(t("city.import.validationFailed"));
      setImportResults({
        success: 0,
        failed: validationErrors.length,
        errors: validationErrors,
      });
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    const results = { success: 0, failed: 0, errors: [] };

    for (let i = 0; i < importData.length; i++) {
      const city = importData[i];

      try {
        await new Promise((resolve) => {
          createCity(city, {
            onSuccess: () => {
              results.success++;
              resolve();
            },
            onError: (error) => {
              results.failed++;
              results.errors.push(
                `${city.name}: ${
                  error.message || t("city.import.unknownError")
                }`
              );
              resolve(); // Continue with other cities
            },
          });
        });
      } catch (error) {
        results.failed++;
        results.errors.push(
          `${city.name}: ${error.message || t("city.import.unknownError")}`
        );
      }

      setImportProgress(((i + 1) / importData.length) * 100);
    }

    setImportResults(results);
    setIsImporting(false);

    if (results.success > 0) {
      toast.success(
        t("city.import.partialSuccess", {
          success: results.success,
          total: importData.length,
        })
      );
    }

    if (results.failed > 0) {
      toast.error(t("city.import.someErrors", { failed: results.failed }));
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportData([]);
    setImportProgress(0);
    setImportResults(null);
    setIsImporting(false);
  };

  return (
    <div className="flex gap-2">
      {/* Export Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={!cities.length}
        className="text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
      >
        <Download className="h-4 w-4 mr-1" />
        {t("city.export.button")}
      </Button>

      {/* Import Dialog */}
      <Dialog
        open={isImportOpen}
        onOpenChange={(open) => {
          setIsImportOpen(open);
          if (!open) resetImport();
        }}
      >
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="text-blue-600 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <Upload className="h-4 w-4 mr-1" />
            {t("city.import.button")}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t("city.import.title")}
            </DialogTitle>
            <DialogDescription>
              {t("city.import.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="import-file">{t("city.import.selectFile")}</Label>
              <Input
                id="import-file"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={isImporting}
              />
            </div>

            {/* CSV Format Info */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="text-sm">
                  <div className="font-medium mb-1">
                    {t("city.import.formatInfo.title")}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {t("city.import.formatInfo.columns")}: Name, Arabic Name,
                    Deadline, Delivery Fees, Cancellation Fees, Refusal Fees,
                    Fixed Fees (TRUE/FALSE)
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            {/* Preview */}
            {importData.length > 0 && !isImporting && !importResults && (
              <div className="space-y-2">
                <Label>
                  {t("city.import.preview")} ({importData.length}{" "}
                  {t("city.import.cities")})
                </Label>
                <div className="max-h-40 overflow-y-auto border rounded p-2 bg-gray-50 dark:bg-gray-800">
                  {importData.slice(0, 5).map((city, index) => (
                    <div key={index} className="text-sm py-1">
                      {city.name} | {city.nameArabic} | {city.deliveryFees}{" "}
                      {t("main.currency")}
                    </div>
                  ))}
                  {importData.length > 5 && (
                    <div className="text-sm text-gray-500">
                      {t("city.import.andMore", {
                        count: importData.length - 5,
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Import Progress */}
            {isImporting && (
              <div className="space-y-2">
                <Label>{t("city.import.importing")}</Label>
                <Progress value={importProgress} className="w-full" />
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {Math.round(importProgress)}% {t("common.complete")}
                </div>
              </div>
            )}

            {/* Import Results */}
            {importResults && (
              <div className="space-y-2">
                <Label>{t("city.import.results")}</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    {t("city.import.successful")}: {importResults.success}
                  </div>
                  {importResults.failed > 0 && (
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      {t("city.import.failed")}: {importResults.failed}
                    </div>
                  )}
                </div>

                {importResults.errors.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border rounded p-2 bg-red-50 dark:bg-red-900/20">
                    {importResults.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-600 py-1">
                        {error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsImportOpen(false)}
              disabled={isImporting}
            >
              {importResults ? t("common.close") : t("common.cancel")}
            </Button>
            {importData.length > 0 && !importResults && (
              <Button
                onClick={handleImport}
                disabled={isImporting || importData.length === 0}
              >
                {isImporting
                  ? t("city.import.importing")
                  : t("city.import.start", { count: importData.length })}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ImportExportCities;
